// src/components/SearchPage.js
import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';

const SearchPage = ({ 
  products, 
  searchTerm, 
  onClose, 
  primaryColor, 
  secondaryColor, 
  contacts, 
  categories,
  language = 'en' // 'en' or 'km'
}) => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Translations
  const translations = {
    en: {
      searchResults: 'Search Results',
      found: 'Found',
      resultsFor: 'results for',
      noProductsFound: 'No products found',
      tryDifferentKeywords: 'Try searching with different keywords or browse our categories',
      continueShopping: 'Continue Shopping',
      viewProduct: 'View Product',
      view: 'View',
      noDescription: 'No description available',
      inStock: 'in stock',
      outOfStock: 'Out of stock',
      grid: 'Grid',
      list: 'List'
    },
    km: {
      searchResults: 'លទ្ធផលស្វែងរក',
      found: 'បានរកឃើញ',
      resultsFor: 'លទ្ធផលសម្រាប់',
      noProductsFound: 'មិនមានផលិតផល',
      tryDifferentKeywords: 'សូមសាកល្បងស្វែងរកដោយពាក្យគន្លឹះផ្សេងទៀត ឬរកមើលតាមប្រភេទរបស់យើង',
      continueShopping: 'បន្តការទិញទំនិញ',
      viewProduct: 'មើលផលិតផល',
      view: 'មើល',
      noDescription: 'គ្មានការពិពណ៌នា',
      inStock: 'មានស្តុក',
      outOfStock: 'អស់ស្តុក',
      grid: 'តារាង',
      list: 'បញ្ជី'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(product => 
        product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, products]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const discount = (price, discountPrice) => {
    if (!discountPrice || !price) return 0;
    return Math.round(((price - discountPrice) / price) * 100);
  };

  // Get image URL with safety check
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/96x96?text=No+Image';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath;
    return `https://easymenu-backend-4w2d.onrender.com${imagePath}`;
  };

  // Safety check - if products is undefined or not array
  const safeProducts = Array.isArray(products) ? products : [];

  return (
    <>
      <div 
        className="fixed inset-0 bg-white z-50 overflow-y-auto"
        style={{ fontFamily: getFontFamily() }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 
                  className="text-xl md:text-2xl font-semibold text-gray-800"
                  style={{ fontFamily: getFontFamily() }}
                >
                  🔍 {t.searchResults}
                </h2>
                <p 
                  className="text-sm text-gray-500 mt-0.5"
                  style={{ fontFamily: getFontFamily() }}
                >
                  {t.found} {filteredProducts.length} {t.resultsFor} "{searchTerm}"
                </p>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                {/* View Toggle Buttons */}
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm transition-all duration-200 ${
                      viewMode === 'grid' 
                        ? 'text-white' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                    style={{ 
                      backgroundColor: viewMode === 'grid' ? primaryColor : 'transparent',
                      fontFamily: getFontFamily()
                    }}
                    title={t.grid}
                  >
                    <i className="fas fa-th"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm transition-all duration-200 ${
                      viewMode === 'list' 
                        ? 'text-white' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                    style={{ 
                      backgroundColor: viewMode === 'list' ? primaryColor : 'transparent',
                      fontFamily: getFontFamily()
                    }}
                    title={t.list}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-full"
                  aria-label={language === 'km' ? 'បិទ' : 'Close'}
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          {filteredProducts.length === 0 ? (
            // No Results
            <div className="text-center py-12 md:py-20">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-search text-gray-300 text-4xl"></i>
              </div>
              <h3 
                className="text-xl font-medium text-gray-600 mb-2"
                style={{ fontFamily: getFontFamily() }}
              >
                {t.noProductsFound}
              </h3>
              <p 
                className="text-gray-400 max-w-md mx-auto"
                style={{ fontFamily: getFontFamily() }}
              >
                {t.tryDifferentKeywords}
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md"
                style={{ 
                  backgroundColor: primaryColor,
                  fontFamily: getFontFamily()
                }}
              >
                {t.continueShopping}
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            // Grid View
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id} onClick={() => handleProductClick(product)}>
                  <ProductCard
                    product={product}
                    onClick={() => handleProductClick(product)}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    language={language}
                  />
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div className="space-y-3">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="bg-white border border-gray-200 hover:shadow-lg rounded-xl transition-all duration-200 cursor-pointer group overflow-hidden"
                >
                  <div className="flex flex-col sm:flex-row gap-4 p-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0 w-full sm:w-28 h-28 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={getImageUrl(product.images && product.images[0])}
                        alt={product.title || 'Product'}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/112x112?text=No+Image';
                        }}
                      />
                    </div>
                    
                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 
                        className="text-base font-semibold text-gray-800 mb-1"
                        style={{ fontFamily: getFontFamily() }}
                      >
                        {product.title || 'Untitled Product'}
                      </h3>
                      <p 
                        className="text-sm text-gray-500 mb-2 line-clamp-2"
                        style={{ fontFamily: getFontFamily() }}
                      >
                        {product.description || t.noDescription}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        {discount(product.price, product.discount_price) > 0 && (
                          <span className="bg-red-500 text-white px-2 py-0.5 text-xs font-semibold rounded-full">
                            -{discount(product.price, product.discount_price)}% OFF
                          </span>
                        )}
                        {product.stock !== undefined && (
                          <span className={`text-xs font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <i className={`fas ${product.stock > 0 ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                            {product.stock > 0 ? `${product.stock} ${t.inStock}` : t.outOfStock}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {product.discount_price ? (
                          <>
                            <span 
                              className="text-lg font-bold"
                              style={{ color: primaryColor, fontFamily: getFontFamily() }}
                            >
                              ${parseFloat(product.discount_price).toFixed(2)}
                            </span>
                            <span className="text-gray-400 line-through text-sm">
                              ${parseFloat(product.price).toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span 
                            className="text-lg font-bold"
                            style={{ color: primaryColor, fontFamily: getFontFamily() }}
                          >
                            ${parseFloat(product.price).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* View Button */}
                    <div className="flex-shrink-0 self-center sm:self-auto">
                      <button
                        className="px-5 py-2.5 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90 hover:scale-105 flex items-center gap-2 shadow-sm"
                        style={{ 
                          backgroundColor: primaryColor,
                          fontFamily: getFontFamily()
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product);
                        }}
                      >
                        <i className="fas fa-eye"></i>
                        <span className="hidden sm:inline">{t.viewProduct}</span>
                        <span className="sm:hidden">{t.view}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={handleCloseModal}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          contacts={contacts}
          categories={categories}
          language={language}
        />
      )}
    </>
  );
};

export default SearchPage;
